import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://myssa1.osc-fr1.scalingo.io/api';

const LABEL_STYLE = {
  display: 'block', fontSize: '12px', fontWeight: 600,
  color: '#6c757d', marginBottom: '5px',
};

const INPUT_STYLE = {
  width: '100%', padding: '9px 12px',
  border: '1.5px solid #ddd', borderRadius: '8px',
  fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit',
};

const Btn = ({ children, variant = 'primary', onClick, disabled, style: s, type }) => {
  const base = {
    border: 'none', borderRadius: '7px', padding: '8px 16px',
    cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600,
    fontSize: '13px', transition: 'opacity 0.2s', opacity: disabled ? 0.6 : 1,
    width: '100%',
  };
  const variants = {
    primary: { background: '#3498db', color: 'white' },
    success: { background: 'linear-gradient(135deg, #3a7d44, #66bb6a)', color: 'white' },
    danger:  { background: '#e74c3c', color: 'white' },
    warning: { background: '#f39c12', color: 'white' },
    ghost:   { background: '#f0f0f0', color: '#555' },
    active:  { background: '#2c3e50', color: 'white' },
  };
  return (
    <button type={type || 'button'} style={{ ...base, ...variants[variant], ...s }} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
};

const Statut_COLORS = {
  ouvert:     { bg: '#fde8e8', color: '#c0392b', border: '#e74c3c', label: 'Ouvert' },
  'en cours': { bg: '#fef9e7', color: '#b7770d', border: '#f39c12', label: 'En cours' },
  'soldé':    { bg: '#e8fdf0', color: '#1a7a40', border: '#2ecc71', label: 'Soldé' },
};

const Taches = () => {
  const [taches, setTaches]                 = useState([]);
  const [filteredTaches, setFilteredTaches] = useState([]);
  const [columns, setColumns]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [activeStatut, setActiveStatut]     = useState('tous');
  const [activeDept, setActiveDept]         = useState('tous');
  const [statusCounts, setStatusCounts]     = useState({ ouvert: 0, enCours: 0, solde: 0 });
  const [formData, setFormData]             = useState({ DE: '', Departement: '', Description: '' });
  const [submitting, setSubmitting]         = useState(false);


   useEffect(() => {
      document.title = 'MYSSA – Tâches';
    }, []);
  


  /* ── Chargement ── */
  useEffect(() => {
    const fetchTaches = async () => {
      try {
        const response = await fetch(`${API}/taches`);
        if (!response.ok) throw new Error('Erreur lors du chargement');
        let data = await response.json();
data = data.map((t) => {
  const { ID, Client_id, Date: rawDate, ...rest } = t;
  const dateObj = new Date(rawDate);
  const formattedDate = isNaN(dateObj) ? '' :
    `${String(dateObj.getDate()).padStart(2,'0')}/${String(dateObj.getMonth()+1).padStart(2,'0')}/${dateObj.getFullYear()}`;
  const statutKey = Object.keys(rest).find(k => k.toLowerCase() === 'statut');
  return { ...rest, Date: formattedDate, _rawDate: dateObj, Statut: rest[statutKey] || 'ouvert' };
})
.sort((a, b) => (a._rawDate || 0) - (b._rawDate || 0))
.map(({ _rawDate, ...rest }) => rest);
        setTaches(data);
        setFilteredTaches(data);
        if (data.length > 0) setColumns(Object.keys(data[0]));
        const counts = { ouvert: 0, enCours: 0, solde: 0 };
        data.forEach(t => {
          if (t.Statut === 'ouvert') counts.ouvert++;
          else if (t.Statut === 'en cours') counts.enCours++;
          else if (t.Statut === 'soldé') counts.solde++;
        });
        setStatusCounts(counts);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTaches();
  }, []);

  /* ── Filtres combinés ── */
  const applyFilters = (statut, dept, data = taches) => {
    let result = data;
    if (statut !== 'tous') result = result.filter(t => t.Statut === statut);
    if (dept !== 'tous')   result = result.filter(t => (t['Département'] || t['Departement']) === dept);
    setFilteredTaches(result);
  };

  const handleStatutFilter = (f) => { setActiveStatut(f); applyFilters(f, activeDept); };
  const handleDeptFilter   = (d) => { setActiveDept(d);   applyFilters(activeStatut, d); };

  /* ── Formulaire ── */
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/taches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Description: formData.Description, Departement: formData.Departement, DE: formData.DE, source: 'tache' }),
      });
      const data = await response.json();
      if (data.success) {
        const now = new Date();
        const newTache = {
          'Numero tache': data.numero_tache,
          De: formData.DE, Département: formData.Departement,
          Description: formData.Description, Statut: 'ouvert',
          Date: `${now.getDate()}/${now.getMonth()+1}/${now.getFullYear()}`,
        };
        const updated = [...taches, newTache];
        setTaches(updated);
        applyFilters(activeStatut, activeDept, updated);
        setStatusCounts(prev => ({ ...prev, ouvert: prev.ouvert + 1 }));
        setFormData({ DE: '', Departement: '', Description: '' });
      } else { alert('Erreur : ' + data.error); }
    } catch { alert('Erreur serveur'); }
    finally { setSubmitting(false); }
  };

  /* ── Changement statut ── */
  const handleStatutChange = async (rowIndex, tache, newStatus) => {
    const updated = [...filteredTaches];
    const oldStatus = tache.Statut;
    updated[rowIndex] = { ...updated[rowIndex], Statut: newStatus };
    setFilteredTaches(updated);
    setStatusCounts(prev => ({
      ouvert:  prev.ouvert  + (newStatus === 'ouvert'   ? 1 : 0) - (oldStatus === 'ouvert'   ? 1 : 0),
      enCours: prev.enCours + (newStatus === 'en cours' ? 1 : 0) - (oldStatus === 'en cours' ? 1 : 0),
      solde:   prev.solde   + (newStatus === 'soldé'    ? 1 : 0) - (oldStatus === 'soldé'    ? 1 : 0),
    }));
    try {
      const response = await fetch(`${API}/taches/${tache['Numero tache']}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatus }),
      });
      const data = await response.json();
      if (!data.success) alert('Erreur mise à jour : ' + data.error);
    } catch { alert('Erreur serveur'); }
  };

  /* ── Camembert ── */
  const chartData = {
    labels: ['Ouvert', 'En cours', 'Soldé'],
    datasets: [{
      data: [statusCounts.ouvert, statusCounts.enCours, statusCounts.solde],
      backgroundColor: ['#fde8e8', '#fef9e7', '#e8fdf0'],
      borderColor: ['#e74c3c', '#f39c12', '#2ecc71'],
      borderWidth: 2,
    }],
  };
  const chartOptions = {
    plugins: {
      legend: { display: false },
      datalabels: {
        color: '#2c3e50',
        formatter: (value, context) => value > 0 ? `${context.chart.data.labels[context.dataIndex]} : ${value}` : '',
        font: { weight: 'bold', size: 12 },
      },
    },
  };

  if (loading) return <div style={{ padding: '28px 32px', fontFamily: "'Segoe UI', sans-serif", color: '#888' }}>Chargement…</div>;
  if (error)   return <div style={{ padding: '28px 32px', fontFamily: "'Segoe UI', sans-serif", color: '#e74c3c' }}>Erreur : {error}</div>;

  return (
    <div style={{ padding: '28px 32px', fontFamily: "'Segoe UI', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#2c3e50' }}>✅ Gestion des tâches</h1>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {[
            { count: statusCounts.ouvert,  label: 'Ouvert',   bg: '#fde8e8', color: '#c0392b', border: '#e74c3c', plural: 's' },
            { count: statusCounts.enCours, label: 'En cours', bg: '#fef9e7', color: '#b7770d', border: '#f39c12', plural: '' },
            { count: statusCounts.solde,   label: 'Soldé',    bg: '#e8fdf0', color: '#1a7a40', border: '#2ecc71', plural: 's' },
          ].map(({ count, label, bg, color, border, plural }) => (
            <span key={label} style={{ padding: '4px 10px', borderRadius: '20px', background: bg, color, fontWeight: 700, border: `1.5px solid ${border}`, fontSize: '13px' }}>
              {count} {label}{count > 1 ? plural : ''}
            </span>
          ))}
        </div>
      </div>


{/* ── Formulaire + Camembert ── */}
<div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', marginBottom: '20px' }}>
  <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '24px', flex: 1, minWidth: 0 }}>

    <form onSubmit={handleSubmit}>
      <div style={{ position: 'relative', height: '210px' }}>

        {/* De */}
        <div style={{ position: 'absolute', left: '-150px', top: '0px', width: '200px' }}>
          <label style={LABEL_STYLE}>De</label>
          <input name="DE" value={formData.DE} onChange={handleChange} required
            placeholder="Nom / service" style={INPUT_STYLE} />
        </div>

        {/* Département */}
        <div style={{ position: 'absolute', left: '300px', top: '0px', width: '200px' }}>
          <label style={LABEL_STYLE}>Département</label>
          <input name="Departement" value={formData.Departement} onChange={handleChange} required
            placeholder="ex: APPRO" style={INPUT_STYLE} />
        </div>

        {/* Description */}
        <div style={{ position: 'absolute', left: '-150px', top: '80px', width: '1500px' }}>
          <label style={LABEL_STYLE}>Description</label>
          <textarea name="Description" value={formData.Description} onChange={handleChange} required
            placeholder="Décrivez la tâche…" rows={5}
            style={{ ...INPUT_STYLE, resize: 'vertical' }} />
        </div>

        {/* Bouton */}
        <div style={{ position: 'absolute', left: '750px', top: '25px', width: '200px' }}>
          <Btn variant="success" disabled={submitting} type="submit">
            {submitting ? 'Enregistrement…' : 'Enregistrer'}
          </Btn>
        </div>

      </div>
    </form>
  </div>


</div>


      {/* ── Formulaire + Camembert ── */}
   

      {/* ── Filtres — une seule ligne ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center' }}>

        {/* Statut */}
        {[
          { key: 'ouvert',   label: '🔴 Ouvert',  activeVariant: 'danger' },
          { key: 'en cours', label: '🟡 En cours', activeVariant: 'warning' },
          { key: 'soldé',    label: '🟢 Soldé',    activeVariant: 'success' },
          { key: 'tous',     label: 'Tous',         activeVariant: 'primary' },
        ].map(({ key, label, activeVariant }) => (
          <Btn
            key={key}
            variant={activeStatut === key ? activeVariant : 'ghost'}
            onClick={() => handleStatutFilter(key)}
            style={{ width: 'auto' }}
          >
            {label}
          </Btn>
        ))}

        {/* Séparateur */}
        <span style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 4px', flexShrink: 0 }} />

        {/* Département */}
        {[
          { key: 'tous',  label: 'Tous' },
          { key: 'APPRO', label: 'APPRO' },
          { key: 'ADV',   label: 'ADV' },
          { key: 'LOG',   label: 'LOG' },
        ].map(({ key, label }) => (
          <Btn
            key={`dept-${key}`}
            variant={activeDept === key ? 'active' : 'ghost'}
            onClick={() => handleDeptFilter(key)}
            style={{ width: 'auto' }}
          >
            {label}
          </Btn>
        ))}

        {/* Compteur */}
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#888', alignSelf: 'center' }}>
          {filteredTaches.length} tâche{filteredTaches.length > 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Tableau ── */}
      <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '460px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                {columns.map(col => (
                  <th key={col} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#6c757d', borderBottom: '2px solid #e9ecef', whiteSpace: 'nowrap' }}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredTaches.length === 0 && (
                <tr><td colSpan={columns.length} style={{ padding: '24px', textAlign: 'center', color: '#aaa' }}>Aucune tâche trouvée</td></tr>
              )}
              {filteredTaches.map((tache, rowIndex) => {
                const sc = Statut_COLORS[tache.Statut] || {};
                return (
                  <tr key={rowIndex} style={{ borderBottom: '1px solid #f0f0f0', background: rowIndex % 2 === 0 ? 'white' : '#fafbfc' }}>
                    {columns.map(col => (
                      <td key={col} style={{
                        padding: '11px 14px', color: '#2c3e50',
                        verticalAlign: col === 'Description' || col === 'Commentaires' ? 'top' : 'middle',
                        whiteSpace: col === 'Description' || col === 'Commentaires' ? 'pre-wrap' : 'nowrap',
                        wordBreak: col === 'Description' || col === 'Commentaires' ? 'break-word' : 'normal',
                        maxWidth: col === 'Description' || col === 'Commentaires' ? '280px' : 'none',
                      }}>
                        {col === 'Statut' ? (
                          <select
                            value={tache.Statut}
                            onChange={(e) => handleStatutChange(rowIndex, tache, e.target.value)}
                            style={{ padding: '4px 8px', borderRadius: '20px', fontWeight: 700, fontSize: '12px', border: `1.5px solid ${sc.border || '#ccc'}`, background: sc.bg || '#eee', color: sc.color || '#333', cursor: 'pointer', outline: 'none' }}
                          >
                            <option value="ouvert">Ouvert</option>
                            <option value="en cours">En cours</option>
                            <option value="soldé">Soldé</option>
                          </select>
                        ) : (tache[col] ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Taches;